import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { snapshotsApi, Snapshot } from '@/lib/snapshots';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';

export function SharedSnapshotPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery<Snapshot>({
    queryKey: ['snapshot', id],
    queryFn: () => snapshotsApi.getPublic(id!),
    enabled: !!id,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        {isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
        {error && (
          <div className="rounded-2xl bg-white border border-ink-200 p-8 text-center text-ink-600">
            <p className="text-sm">{t('snapshot.not_found')}</p>
          </div>
        )}
        {data && (
          <>
            <div>
              <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-accent-700 bg-accent-50 rounded-md px-2 py-0.5 mb-2">
                {t('snapshot.tag')} · {data.kind}
              </div>
              <h1 className="text-2xl font-semibold text-ink-900 tracking-tight">{data.label}</h1>
              <p className="mt-1 text-xs text-ink-500 num">
                {t('snapshot.captured_label')} {new Date(data.createdAt).toLocaleString()}
                {data.expiresAt && (
                  <>
                    {' '}· {t('snapshot.expires_label')} {new Date(data.expiresAt).toLocaleDateString()}
                  </>
                )}
              </p>
            </div>
            <section className="rounded-2xl bg-white border border-ink-200 p-5">
              <h2 className="text-xs uppercase tracking-wide font-medium text-ink-500 mb-3">
                {t('snapshot.data_section')}
              </h2>
              <SnapshotBody kind={data.kind} payload={data.payload} t={t} />
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * A2: render the snapshot payload as a formatted report per `kind` — never
 * raw JSON.stringify on a public, customer-facing page.
 */
function SnapshotBody({
  kind,
  payload,
  t,
}: {
  kind: string;
  payload: Record<string, unknown>;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const items = Array.isArray((payload as { items?: unknown }).items)
    ? ((payload as { items: Array<Record<string, unknown>> }).items)
    : null;

  if (kind === 'signal_feed' && items) {
    return (
      <ul className="space-y-2">
        {items.map((s, i) => {
          const p = (s.payload ?? {}) as Record<string, unknown>;
          const title = String(p.title ?? s.kind ?? '');
          const subtitle = String(p.subtitle ?? '');
          const when = s.occurredAt ? new Date(String(s.occurredAt)).toLocaleString() : '';
          return (
            <li key={i} className="rounded-lg border border-ink-200 bg-white p-3 flex items-start gap-3">
              <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-2 py-0.5 bg-ink-100 text-ink-600">
                {String(s.kind ?? '').replace(/_/g, ' ')}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink-900 truncate">{title}</div>
                {subtitle && <div className="text-xs text-ink-500 truncate">{subtitle}</div>}
                <div className="text-[11px] text-ink-400 num mt-0.5">{when}</div>
              </div>
              {typeof s.score === 'number' && (
                <span className="shrink-0 num text-sm font-medium text-ink-700">{s.score}</span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  // Generic fallback — a styled key/value table (scalars), never a raw dump.
  const entries = Object.entries(payload).filter(([, v]) => v == null || typeof v !== 'object');
  if (entries.length === 0) {
    return <p className="text-sm text-ink-500">{t('snapshot.empty', { defaultValue: 'No data in this snapshot.' })}</p>;
  }
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="sm:contents">
          <dt className="text-ink-500">{k}</dt>
          <dd className="text-ink-800 num">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
